import { useEffect, useMemo, useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useMetaFunnel } from '@/hooks/useMetaFunnel';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

// =============================================================
// Embudo en vivo (Meta Pixel)
// =============================================================
// Muestra los conteos de PageView, ViewContent, AddToCart, InitiateCheckout y
// Purchase de los últimos 7 o 30 días (rango seleccionable), con las tasas de
// conversión entre pasos y el valor total vendido del evento Purchase.
// Los datos salen de la tabla `meta_pixel_events` (registrada por el Pixel).

interface FunnelStep {
  key: string;
  label: string;
  short: string;
  icon: string;
  color: string;
  hint: string;
}

const STEPS: FunnelStep[] = [
  { key: 'PageView', label: 'Vistas de página', short: 'PageView', icon: 'ri-eye-line', color: 'oklch(var(--accent-500))', hint: 'Todas las visitas al sitio' },
  { key: 'ViewContent', label: 'Vistas de producto', short: 'ViewContent', icon: 'ri-shopping-bag-3-line', color: 'oklch(var(--primary-500))', hint: 'Abrieron una ficha de producto' },
  { key: 'AddToCart', label: 'Añadir al carrito', short: 'AddToCart', icon: 'ri-shopping-cart-2-line', color: 'oklch(var(--secondary-500))', hint: 'Agregaron o ajustaron un producto' },
  { key: 'InitiateCheckout', label: 'Iniciar checkout', short: 'InitiateCheckout', icon: 'ri-bank-card-line', color: '#0d9488', hint: 'Entraron a la pantalla de pago' },
  { key: 'Purchase', label: 'Compras', short: 'Purchase', icon: 'ri-check-double-line', color: '#059669', hint: 'Pedidos confirmados' },
];

const RANGE_OPTIONS = [7, 30] as const;
const REFRESH_MS = 30000;

// Genera las fechas (YYYY-MM-DD) de los últimos N días para el eje del gráfico.
function lastNDays(n: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    out.push(d.toLocaleDateString('en-CA'));
  }
  return out;
}

function formatDayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
}

function pct(part: number, whole: number): number {
  if (!whole || whole <= 0) return 0;
  return (part / whole) * 100;
}

// Formatea un monto en pesos colombianos.
function formatCop(n: number): string {
  return `$${Math.round(n).toLocaleString('es-CO')}`;
}

export default function MetaFunnelPage() {
  const [range, setRange] = useState<number>(7);
  const { data, loading, error, refetch } = useMetaFunnel(range);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Marca la hora de la última carga exitosa.
  useEffect(() => {
    if (!loading) setLastUpdated(new Date());
  }, [loading, data]);

  // Auto-refresco cada 30s mientras el interruptor esté activo.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => {
      refetch();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, refetch]);

  const totals = useMemo(() => {
    const map: Record<string, number> = {};
    data.forEach((row) => {
      map[row.eventName] = (map[row.eventName] ?? 0) + row.total;
    });
    return map;
  }, [data]);

  // Valor total vendido (COP) del evento Purchase en el rango seleccionado.
  const purchaseValue = useMemo(
    () => data.reduce((sum, row) => (row.eventName === 'Purchase' ? sum + row.totalValue : sum), 0),
    [data],
  );

  const grandTotal = STEPS.reduce((sum, s) => sum + (totals[s.key] ?? 0), 0);
  const maxCount = Math.max(1, totals[STEPS[0].key] ?? 0, ...STEPS.map((s) => totals[s.key] ?? 0));

  const chartData = useMemo(() => {
    const days = lastNDays(range);
    const byDay: Record<string, Record<string, number>> = {};
    data.forEach((row) => {
      if (!byDay[row.day]) byDay[row.day] = {};
      byDay[row.day][row.eventName] = (byDay[row.day][row.eventName] ?? 0) + row.total;
    });
    return days.map((day) => {
      const base: Record<string, string | number> = { day, label: formatDayLabel(day) };
      STEPS.forEach((s) => {
        base[s.short] = byDay[day]?.[s.key] ?? 0;
      });
      return base;
    });
  }, [data, range]);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-6 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950 flex items-center gap-2">
              Embudo en vivo
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                En vivo
              </span>
            </h1>
            <p className="text-sm text-foreground-500 mt-0.5">
              Eventos del Pixel de Meta · últimos {range} días
              {lastUpdated && (
                <span className="text-foreground-400">
                  {' '}· actualizado {lastUpdated.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center rounded-full bg-background-100 p-1">
              {RANGE_OPTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setRange(d)}
                  className={`text-sm px-3 py-1.5 rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                    range === d
                      ? 'bg-white text-foreground-900 font-medium'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  {d} días
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setAutoRefresh((v) => !v)}
              className={`inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
                autoRefresh
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-background-200 bg-white text-foreground-600'
              }`}
              aria-pressed={autoRefresh}
            >
              <i className={autoRefresh ? 'ri-pause-circle-line' : 'ri-play-circle-line'}></i>
              {autoRefresh ? 'Auto cada 30s' : 'Auto pausado'}
            </button>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-sm px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
              Actualizar
            </button>
          </div>
        </div>

        {error && <ErrorState message={error} onRetry={refetch} />}

        {loading && data.length === 0 && !error ? (
          <div className="bg-white rounded-2xl border border-background-200 p-10 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-accent-500 text-2xl"></i>
          </div>
        ) : grandTotal === 0 && !error ? (
          <div className="bg-white rounded-2xl border border-background-200 p-10 text-center">
            <span className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
              <i className="ri-filter-3-line text-2xl"></i>
            </span>
            <p className="text-foreground-800 font-semibold mb-1">Aún no hay eventos registrados</p>
            <p className="text-sm text-foreground-500 max-w-md mx-auto">
              Los conteos aparecen a medida que los visitantes navegan la tienda con el Pixel activo.
              Verifica que el Pixel esté <strong>Activado</strong> en Configuración → Marketing &amp; Analítica.
            </p>
          </div>
        ) : (
          <>
            {/* KPIs rápidos */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <KpiTile label="Visitas (PageView)" value={totals.PageView ?? 0} icon="ri-eye-line" tone="accent" />
              <KpiTile label="Valor vendido (COP)" value={formatCop(purchaseValue)} icon="ri-money-dollar-circle-line" tone="primary" />
              <KpiTile
                label="Conversión a compra"
                value={`${pct(totals.Purchase ?? 0, totals.PageView ?? 0).toFixed(1)}%`}
                icon="ri-percent-line"
                tone="emerald"
              />
              <KpiTile label="Checkouts iniciados" value={totals.InitiateCheckout ?? 0} icon="ri-bank-card-line" tone="teal" />
              <KpiTile label="Compras confirmadas" value={totals.Purchase ?? 0} icon="ri-check-double-line" tone="secondary" />
            </div>

            {/* Embudo */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
              <h3 className="text-sm font-semibold text-foreground-900 mb-1 flex items-center gap-2">
                <i className="ri-filter-3-line text-accent-600"></i>Embudo de conversión
              </h3>
              <p className="text-xs text-foreground-500 mb-5">Del primer clic hasta la compra confirmada.</p>

              <div className="space-y-4">
                {STEPS.map((step, idx) => {
                  const count = totals[step.key] ?? 0;
                  const widthPct = Math.max(2, (count / maxCount) * 100);
                  const fromPrev = idx === 0 ? 100 : pct(count, totals[STEPS[idx - 1].key] ?? 0);
                  const fromTop = pct(count, totals[STEPS[0].key] ?? 0);
                  return (
                    <div key={step.key}>
                      <div className="flex items-center justify-between gap-3 mb-1.5">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-white"
                            style={{ backgroundColor: step.color }}
                          >
                            <i className={`${step.icon} text-sm`}></i>
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-900 truncate">{step.label}</p>
                            <p className="text-[11px] text-foreground-400 truncate hidden sm:block">{step.hint}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-base font-bold text-foreground-950">{count.toLocaleString()}</p>
                          {step.key === 'Purchase' && purchaseValue > 0 && (
                            <p className="text-[11px] font-medium text-emerald-600">{formatCop(purchaseValue)}</p>
                          )}
                          {idx > 0 && (
                            <p className="text-[11px] text-foreground-400">
                              <span className={fromPrev < 30 ? 'text-amber-600' : 'text-emerald-600'}>
                                {fromPrev.toFixed(1)}%
                              </span>{' '}
                              del paso anterior
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="h-3 rounded-full bg-background-100 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${widthPct}%`, backgroundColor: step.color }}
                        ></div>
                      </div>
                      {idx > 0 && (
                        <p className="text-[11px] text-foreground-400 mt-1">
                          {fromTop.toFixed(1)}% respecto al total de visitas
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tendencia diaria */}
            <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
              <h3 className="text-sm font-semibold text-foreground-900 mb-1 flex items-center gap-2">
                <i className="ri-line-chart-line text-accent-600"></i>Tendencia diaria
              </h3>
              <p className="text-xs text-foreground-500 mb-4">Eventos por día en los últimos {range} días.</p>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-200))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }} width={40} />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 10,
                      border: '1px solid oklch(var(--background-200))',
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {STEPS.map((s) => (
                    <Line
                      key={s.short}
                      type="monotone"
                      dataKey={s.short}
                      name={s.label}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function KpiTile({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: string | number;
  icon: string;
  tone: 'accent' | 'primary' | 'emerald' | 'teal' | 'secondary';
}) {
  const toneCls = {
    accent: 'bg-accent-100 text-accent-700',
    primary: 'bg-primary-100 text-primary-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    teal: 'bg-teal-100 text-teal-700',
    secondary: 'bg-secondary-100 text-secondary-900',
  }[tone];
  return (
    <div className="bg-white rounded-xl border border-background-200 p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-foreground-500 truncate">{label}</p>
          <p className="text-xl font-bold text-foreground-950 mt-1 truncate">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
        <div className={`w-9 h-9 rounded-lg ${toneCls} flex items-center justify-center flex-shrink-0 ml-2`}>
          <i className={`${icon} text-sm`}></i>
        </div>
      </div>
    </div>
  );
}